import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CreditCard, Bell, Wallet, Smartphone, QrCode, Clock, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../context/LanguageContext';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: 'easeOut' },
};

const staggerContainer = {
  animate: {
    transition: { staggerChildren: 0.1 },
  },
};

export default function Colmados() {
  const { t } = useLanguage();

  const benefits = [
    {
      icon: Smartphone,
      title: t('colmados.benefit1'),
      description: t('colmados.benefit1Desc'),
      color: 'emerald',
    },
    {
      icon: QrCode,
      title: t('colmados.benefit2'),
      description: t('colmados.benefit2Desc'),
      color: 'blue',
    },
    {
      icon: Wallet,
      title: t('colmados.benefit3'),
      description: t('colmados.benefit3Desc'),
      color: 'purple',
    },
    {
      icon: Clock,
      title: t('colmados.benefit4'),
      description: t('colmados.benefit4Desc'),
      color: 'amber',
    },
  ];

  const colorClasses = {
    emerald: {
      bg: 'bg-emerald-100',
      icon: 'text-emerald-600',
      hoverBg: 'group-hover:bg-emerald-600',
    },
    blue: {
      bg: 'bg-blue-100',
      icon: 'text-blue-600',
      hoverBg: 'group-hover:bg-blue-600',
    },
    purple: {
      bg: 'bg-purple-100',
      icon: 'text-purple-600',
      hoverBg: 'group-hover:bg-purple-600',
    },
    amber: {
      bg: 'bg-amber-100',
      icon: 'text-amber-600',
      hoverBg: 'group-hover:bg-amber-500',
    },
  };

  return (
    <Layout>
      {/* Hero Section with Real Colmado Image */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-900 via-blue-800 to-emerald-900 py-16 md:py-24" data-testid="colmados-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial="initial"
            animate="animate"
            variants={staggerContainer}
            className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center"
          >
            {/* Image - Primary Visual Anchor */}
            <motion.div variants={fadeInUp} className="relative order-1 lg:order-2">
              <div className="aspect-[4/3] rounded-2xl lg:rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_fintech-platform-32/artifacts/h92by57g_colmado_-_magua.jpg"
                  alt={t('colmados.imageAlt')}
                  loading="eager"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>

            {/* Copy - Tightened */}
            <motion.div variants={fadeInUp} className="order-2 lg:order-1">
              <span className="inline-block text-sm font-semibold text-emerald-400 uppercase tracking-wider mb-3">
                {t('colmados.tagline')}
              </span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-tight">
                {t('colmados.title')}
              </h1>
              <p className="mt-5 text-base lg:text-lg text-blue-100 leading-relaxed max-w-lg">
                {t('colmados.subtitle')}
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 rounded-full">
                  <CheckCircle className="w-4 h-4 text-emerald-400" />
                  <span className="text-sm text-white">{t('colmados.badge1')}</span>
                </div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 rounded-full">
                  <CheckCircle className="w-4 h-4 text-emerald-400" />
                  <span className="text-sm text-white">{t('colmados.badge2')}</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* How Colmados Get Paid - 3-Step Flow */}
      <section className="py-16 md:py-20 bg-white" data-testid="colmados-payment-flow">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-3">
                {t('colmados.paymentFlowTitle')}
              </h2>
              <p className="text-slate-600 max-w-2xl mx-auto">
                {t('colmados.paymentFlowSubtitle')}
              </p>
            </motion.div>

            {/* 3-Step Flow - Horizontal on desktop, vertical on mobile */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-4">
              {/* Step 1 */}
              <motion.div 
                variants={fadeInUp}
                className="relative bg-slate-50 rounded-2xl p-6 text-center"
                data-testid="payment-step-1"
              >
                <div className="w-14 h-14 rounded-full bg-blue-600 text-white flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  1
                </div>
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mx-auto mb-4">
                  <CreditCard className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{t('colmados.step1Title')}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {t('colmados.step1Desc')}
                </p>
                {/* Arrow connector - hidden on mobile */}
                <div className="hidden md:block absolute top-1/2 -right-2 transform -translate-y-1/2 z-10">
                  <ArrowRight className="w-5 h-5 text-slate-300" />
                </div>
              </motion.div>

              {/* Step 2 */}
              <motion.div 
                variants={fadeInUp}
                className="relative bg-slate-50 rounded-2xl p-6 text-center"
                data-testid="payment-step-2"
              >
                <div className="w-14 h-14 rounded-full bg-emerald-600 text-white flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  2
                </div>
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                  <Bell className="w-6 h-6 text-emerald-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{t('colmados.step2Title')}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {t('colmados.step2Desc')}
                </p>
                {/* Arrow connector - hidden on mobile */}
                <div className="hidden md:block absolute top-1/2 -right-2 transform -translate-y-1/2 z-10">
                  <ArrowRight className="w-5 h-5 text-slate-300" />
                </div>
              </motion.div>

              {/* Step 3 */}
              <motion.div 
                variants={fadeInUp}
                className="relative bg-slate-50 rounded-2xl p-6 text-center"
                data-testid="payment-step-3"
              >
                <div className="w-14 h-14 rounded-full bg-purple-600 text-white flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  3
                </div>
                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center mx-auto mb-4">
                  <Wallet className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{t('colmados.step3Title')}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {t('colmados.step3Desc')}
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Merchant Onboarding CTA */}
      <section className="py-14 md:py-16 bg-gradient-to-r from-emerald-600 to-emerald-700" data-testid="colmados-onboarding-cta">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 variants={fadeInUp} className="text-2xl sm:text-3xl font-bold text-white mb-4">
              {t('colmados.onboardingTitle')}
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-emerald-100 mb-6 max-w-xl mx-auto">
              {t('colmados.onboardingSubtitle')}
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Link to="/login">
                <Button
                  data-testid="colmados-onboarding-btn"
                  className="h-12 px-8 rounded-full bg-white text-emerald-700 hover:bg-emerald-50 font-semibold shadow-lg inline-flex items-center gap-2"
                >
                  {t('colmados.onboardingCta')}
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
            </motion.div>
            <motion.p variants={fadeInUp} className="text-xs text-emerald-200 mt-4">
              {t('colmados.onboardingDisclaimer')}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-20 bg-slate-50" data-testid="colmados-benefits">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 variants={fadeInUp} className="text-2xl sm:text-3xl font-bold text-slate-900 text-center mb-10">
              {t('colmados.benefitsTitle')}
            </motion.h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                const colors = colorClasses[benefit.color];
                return (
                  <motion.div
                    key={index}
                    variants={fadeInUp}
                    className="group relative p-6 bg-white rounded-2xl border border-slate-100 hover:border-blue-100 transition-all duration-300 hover:shadow-md"
                    data-testid={`colmados-benefit-${index + 1}`}
                  >
                    <div className={`w-12 h-12 rounded-xl ${colors.bg} flex items-center justify-center mb-4 ${colors.hoverBg} transition-colors`}>
                      <Icon className={`w-6 h-6 ${colors.icon} group-hover:text-white transition-colors`} />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2">{benefit.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{benefit.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-blue-600 to-blue-700" data-testid="colmados-cta-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 variants={fadeInUp} className="text-2xl sm:text-3xl font-bold text-white mb-4">
              {t('colmados.ctaTitle')}
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-blue-100 mb-6 max-w-lg mx-auto">
              {t('colmados.ctaSubtitle')}
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Link to="/login">
                <Button
                  data-testid="colmados-final-cta"
                  className="h-12 px-8 rounded-full bg-white text-blue-700 hover:bg-blue-50 font-semibold shadow-lg inline-flex items-center gap-2"
                >
                  {t('colmados.ctaButton')}
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
